import { OPERATORS } from '../../constants';
import { BaseOperatorStrategy } from './base.operator-strategy';
import type {
  ModeSelectionContext,
  SelectionBlockerArgs,
  SelectionBlockerResult,
  UnitCostCalculationArgs,
} from '../operator-strategy';

/**
 * WeatherEx pricing model:
 *   unitcost  = charged_price_usd fetched from /tasks/weatherex/quote (per refresh)
 *   totalcost = unitcost × noOfRefreshes
 * No holdback/cloudcover/ONA factors apply.
 */
export class WeatherexOperatorStrategy extends BaseOperatorStrategy {
  readonly operatorKey = OPERATORS.WEATHEREX;

  /**
   * Override: set unitcost to the quoted per-refresh price and selectedCost to
   * unitcost × noOfRefreshes. Falls back to latency.unitcost when no live quote
   * is available (e.g. quote fetch failed).
   */
  override calculateUnitCost({ sensorRow }: UnitCostCalculationArgs): void {
    if (!sensorRow) return;

    const unitPrice = sensorRow.quotedUnitPrice != null
      ? Number(sensorRow.quotedUnitPrice)
      : this.fallbackUnitPrice(sensorRow);

    const refreshCount = Number(sensorRow.noOfRefreshes ?? 1);

    // Keep the latency tier display price in sync with the quoted unit price
    if (Array.isArray(sensorRow.latency) && sensorRow.latency[0]) {
      sensorRow.latency[0].cost = unitPrice;
      sensorRow.latency[0].unitcost = unitPrice;
    }
    sensorRow.unitcost = unitPrice;
    // selectedCost drives backend submission total; display in tier always shows unitPrice
    sensorRow.selectedCost = unitPrice * refreshCount;
  }

  private fallbackUnitPrice(_sensorRow: any): number {
    // Return 0 so no static placeholder price is ever shown; only live quotes drive the display.
    return 0;
  }

  /**
   * Base cost implementation kept for compatibility; calculateUnitCost above takes
   * precedence for all live pricing paths.
   */
  protected computeBaseCost(sensorRow: any, latencyIndx: number): number | null {
    if (!sensorRow || !Array.isArray(sensorRow.latency)) return null;

    const selectedLatency = sensorRow.latency[latencyIndx];
    if (!selectedLatency) return null;

    const unitcost = Number(selectedLatency.unitcost ?? 0);
    const refreshCount = Number(sensorRow.noOfRefreshes ?? 1);
    return unitcost * refreshCount;
  }

  /**
   * WeatherEx selection-time blockers (evaluated in order):
   *  1. At least one collection mode must be selected.
   *  2. The live quote must have loaded (unitcost > 0) before the sensor can be enabled.
   *  3. Recurrent orders are not supported.
   */
  getSelectionBlocker(args: SelectionBlockerArgs): SelectionBlockerResult | null {
    const { sensorRow, taskingContext } = args;

    const modes: any[] = Array.isArray(sensorRow?.selectedModes) ? sensorRow.selectedModes : [];
    if (modes.length === 0) {
      return {
        message: 'Please select at least one mode from the options before enabling this sensor.',
        code: 'WEATHEREX_NO_MODES_SELECTED',
        softBlock: true,
      };
    }

    const unitcost = Number(sensorRow?.unitcost ?? 0);
    if (unitcost <= 0) {
      return {
        message: 'The cost is still being calculated. Please wait a moment for the pricing to load before selecting.',
        code: 'WEATHEREX_COST_NOT_LOADED',
        softBlock: true,
      };
    }

    if (taskingContext?.isRecurrent) {
      return this.createUnsupportedRecurrencyBlocker(
        sensorRow,
        'WEATHEREX_RECURRENCY_UNSUPPORTED',
      );
    }

    return null;
  }

  /**
   * Default to the first collection mode (cloud_cover) regardless of target type.
   */
  selectDefaultMode(collectionModes: any[] = [], _context: ModeSelectionContext): any | null {
    return collectionModes[0] ?? null;
  }

  /**
   * All 13 collection modes are available for both AOI and POI.
   */
  isModeDisabled(_mode: any, _sensorRow: any, _context: ModeSelectionContext): boolean {
    return false;
  }

  /**
   * Cancel is blocked once 3 hours have elapsed since the order was placed
   * (per cancel_policy: hours=3, type=>, penalty=100%).
   */
  isCancelBlocked(oprtr: any): boolean {
    if (!oprtr?.orderDate) return false;

    const orderDate = new Date(oprtr.orderDate);
    if (isNaN(orderDate.getTime())) return false;

    const elapsedHours = (Date.now() - orderDate.getTime()) / 3600000;
    return elapsedHours > 3;
  }

  /**
   * The sentence the sensor-name tooltip has always shown for this operator,
   * moved off the three components that each carried their own copy of it.
   * Verbatim — this is a relocation, not a rewording.
   */
  getCancellationPolicyText(): string {
    return 'No cancelation is permitted once refresh is ordered';
  }
}
